import { customElement, observable, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './bookmarks-view.html';
import type { PdfBookmark, Tag } from '../services/api.types';
import {
  deletePdfBookmark,
  getPdfBookmarks,
  getUserSettings,
  updatePdfBookmark,
  upsertUserSetting,
} from '../services/api.service';
import { apiUrl } from '../services/runtime-config';
import { ConfirmDialog } from '../components/confirm-dialog';
import { DateTimeValueConverter } from '../converters/date-time';

const COLUMNS_STORAGE_KEY = 'radish_bookmarks_visible_columns';
const UI_STATE_STORAGE_KEY = 'radish_bookmarks_ui';
const COLUMNS_SETTING_KEY = 'bookmarks.visibleColumns';
const UI_STATE_SETTING_KEY = 'bookmarks.uiState';

export const BOOKMARK_COLUMN_DEFS = [
  { key: 'title', i18nKey: 'bookmarks.columns.title' },
  { key: 'document', i18nKey: 'bookmarks.columns.document' },
  { key: 'file', i18nKey: 'bookmarks.columns.file' },
  { key: 'page', i18nKey: 'bookmarks.columns.page' },
  { key: 'selectedText', i18nKey: 'bookmarks.columns.selectedText' },
  { key: 'description', i18nKey: 'bookmarks.columns.description' },
  { key: 'tags', i18nKey: 'bookmarks.columns.tags' },
  { key: 'createdAt', i18nKey: 'bookmarks.columns.createdAt' },
  { key: 'lastUpdatedAt', i18nKey: 'bookmarks.columns.lastUpdatedAt' },
] as const;

const DEFAULT_COLUMNS: Record<string, boolean> = {
  title: true,
  document: true,
  file: true,
  page: true,
  selectedText: true,
  description: false,
  tags: true,
  createdAt: true,
  lastUpdatedAt: false,
};

interface BookmarkUiState {
  showFilterBar?: boolean;
  filterText?: string;
  filterTags?: Tag[];
  viewMode?: 'list' | 'tiles';
  sortBy?: string;
  sortDescending?: boolean;
}

@customElement({
  name: 'bookmarks-view',
  template,
  dependencies: [DateTimeValueConverter],
})
export class BookmarksView {
  private readonly i18n: I18N = resolve(I18N);

  public bookmarks: PdfBookmark[] = [];
  public isFetching = false;
  public fetchError = '';
  public inlineBookmark: PdfBookmark | null = null;
  public editingBookmark: PdfBookmark | null = null;
  public editTitle = '';
  public editDescription = '';
  public editTags: Tag[] = [];
  public saving = false;
  public editError = '';

  public sortBy = 'createdAt';
  public sortDescending = true;
  public viewMode: 'list' | 'tiles' = 'list';
  public readonly allColumns = BOOKMARK_COLUMN_DEFS;
  public visibleColumns: Record<string, boolean> = { ...DEFAULT_COLUMNS };
  public showColumnPicker = false;
  public showFilterBar = false;
  public filterText = '';
  @observable public filterTags: Tag[] = [];

  public selectedIds: Set<string> = new Set();
  private _lastSelectedIdx = -1;
  private _touchTimer: ReturnType<typeof setTimeout> | null = null;
  private _touchMoved = false;
  private _tapTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastTapBookmarkId = '';
  private _lastTapTime = 0;
  private _preferenceSyncTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly keyHandler = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      if (this.showColumnPicker) {
        this.closeColumnPicker();
      } else if (this.editingBookmark) {
        this.cancelEdit();
      } else if (this.inlineBookmark) {
        this.closeInline();
      }
    }
  };

  public get hasBookmarks(): boolean {
    return this.bookmarks.length > 0;
  }

  public get hasSelection(): boolean {
    return this.selectedIds.size > 0;
  }

  public get selectionCount(): number {
    return this.selectedIds.size;
  }

  public get selectedBookmarks(): PdfBookmark[] {
    return this.bookmarks.filter(bookmark => this.selectedIds.has(bookmark.id));
  }

  public get canDeleteSelection(): boolean {
    return this.selectedBookmarks.length > 0 && !this.saving;
  }

  public get visibleColumnCount(): number {
    return Object.values(this.visibleColumns).filter(Boolean).length;
  }

  public get hasVisibleBookmarks(): boolean {
    return this.visibleBookmarks.length > 0;
  }

  public get hasActiveFilter(): boolean {
    return this.filterText.trim().length > 0 || this.filterTags.length > 0;
  }

  public get visibleBookmarks(): PdfBookmark[] {
    const search = this.filterText.trim().toLocaleLowerCase();
    const selectedTagNames = this.filterTags
      .map(tag => tag.name.trim().toLocaleLowerCase())
      .filter(Boolean);

    return this.bookmarks
      .filter(bookmark => {
        if (search) {
          const haystack = [
            bookmark.title,
            bookmark.documentTitle,
            bookmark.fileName,
            bookmark.pageNumber.toString(),
            bookmark.selectedText,
            bookmark.description,
            bookmark.tags.map(tag => tag.name).join(' '),
          ].join('\n').toLocaleLowerCase();
          if (!haystack.includes(search)) return false;
        }

        return selectedTagNames.every(selectedTag =>
          bookmark.tags.some(tag => tag.name.trim().toLocaleLowerCase() === selectedTag));
      })
      .sort((left, right) => this.compareBookmarks(left, right));
  }

  public get editDirty(): boolean {
    const bookmark = this.editingBookmark;
    return Boolean(bookmark && (
      this.editTitle.trim() !== bookmark.title ||
      this.editDescription !== bookmark.description ||
      this.editTags.map(tag => tag.name).join('\u0000') !==
        bookmark.tags.map(tag => tag.name).join('\u0000')
    ));
  }

  public get editTitleValid(): boolean {
    return this.editTitle.trim().length > 0;
  }

  public get inlinePdfUrl(): string {
    return this.inlineBookmark ? this.pdfUrl(this.inlineBookmark) : '';
  }

  public attached(): void {
    document.addEventListener('keydown', this.keyHandler);
  }

  public detaching(): void {
    document.removeEventListener('keydown', this.keyHandler);
    if (this._touchTimer !== null) clearTimeout(this._touchTimer);
    if (this._tapTimer !== null) clearTimeout(this._tapTimer);
    if (this._preferenceSyncTimer !== null) {
      clearTimeout(this._preferenceSyncTimer);
      this._preferenceSyncTimer = null;
      void this.syncPreferences();
    }
  }

  public async loading(): Promise<void> {
    const localPreferences = this.loadCachedPreferences();
    const settings = await getUserSettings().catch(() => []);
    const serverColumns = settings.find(setting => setting.key === COLUMNS_SETTING_KEY);
    const serverUiState = settings.find(setting => setting.key === UI_STATE_SETTING_KEY);

    if (serverColumns) this.applyColumnsSetting(serverColumns.value);
    if (serverUiState) this.applyUiStateSetting(serverUiState.value);
    if ((!serverColumns && localPreferences.hasColumns) ||
        (!serverUiState && localPreferences.hasUiState)) {
      this.schedulePreferenceSync();
    }

    await this.fetchBookmarks();
  }

  public async fetchBookmarks(): Promise<void> {
    this.isFetching = true;
    this.fetchError = '';
    this.clearSelection();
    try {
      this.bookmarks = await getPdfBookmarks();
    } catch (caughtError) {
      this.fetchError = caughtError instanceof Error
        ? caughtError.message
        : this.i18n.tr('bookmarks.loadFailed');
    } finally {
      this.isFetching = false;
    }
  }

  public toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  public closeColumnPicker(): void {
    this.showColumnPicker = false;
  }

  public toggleColumn(key: string): void {
    this.visibleColumns = { ...this.visibleColumns, [key]: !this.visibleColumns[key] };
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(this.visibleColumns));
    this.schedulePreferenceSync();
  }

  public toggleFilterBar(): void {
    this.showFilterBar = !this.showFilterBar;
    this.saveUiState();
  }

  public onFilterTextInput(event: Event): void {
    this.filterText = (event.target as HTMLInputElement).value;
    this.clearSelection();
    this.saveUiState();
  }

  public clearFilterText(): void {
    this.filterText = '';
    this.clearSelection();
    this.saveUiState();
  }

  public filterTagsChanged(): void {
    this.clearSelection();
    this.saveUiState();
  }

  public clearFilterTags(): void {
    this.filterTags = [];
    this.clearSelection();
    this.saveUiState();
  }

  public sortBy_(column: string): void {
    if (this.sortBy === column) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortBy = column;
      this.sortDescending = true;
    }
    this.clearSelection();
    this.saveUiState();
  }

  public toggleViewMode(): void {
    this.viewMode = this.viewMode === 'list' ? 'tiles' : 'list';
    this.saveUiState();
  }

  public sortIndicator(column: string): string {
    if (this.sortBy !== column) return '';
    return this.sortDescending ? '▼' : '▲';
  }

  public pdfUrl(bookmark: PdfBookmark): string {
    return apiUrl(`/documents/${bookmark.documentId}/files/${bookmark.documentFileId}/download`);
  }

  public clearSelection(): void {
    this.selectedIds = new Set();
    this._lastSelectedIdx = -1;
    this._lastTapBookmarkId = '';
    this._lastTapTime = 0;
  }

  public onTouchStart(bookmark: PdfBookmark): void {
    this._touchMoved = false;
    if (this._touchTimer !== null) clearTimeout(this._touchTimer);
    this._touchTimer = setTimeout(() => {
      this._touchTimer = null;
      this.openInline(bookmark);
    }, 600);
  }

  public onTouchMove(): void {
    this._touchMoved = true;
    if (this._touchTimer !== null) {
      clearTimeout(this._touchTimer);
      this._touchTimer = null;
    }
  }

  public onTouchEnd(bookmark: PdfBookmark, index: number, event: TouchEvent): void {
    if (this._touchTimer !== null) {
      clearTimeout(this._touchTimer);
      this._touchTimer = null;
    }
    if (this._touchMoved) return;
    event.preventDefault();

    const now = Date.now();
    const isDoubleTap = this._lastTapBookmarkId === bookmark.id &&
      (now - this._lastTapTime) < 350;
    if (isDoubleTap) {
      if (this._tapTimer !== null) {
        clearTimeout(this._tapTimer);
        this._tapTimer = null;
      }
      this._lastTapBookmarkId = '';
      this._lastTapTime = 0;
      this.openInline(bookmark);
      return;
    }

    this._lastTapBookmarkId = bookmark.id;
    this._lastTapTime = now;
    if (this._tapTimer !== null) clearTimeout(this._tapTimer);
    this._tapTimer = setTimeout(() => {
      this._tapTimer = null;
      this.selectBookmark(bookmark, index, {
        ctrlKey: false,
        metaKey: false,
        shiftKey: false,
      });
    }, 350);
  }

  public onRowClick(bookmark: PdfBookmark, index: number, event: MouseEvent): void {
    this.selectBookmark(bookmark, index, event);
  }

  public onRowDblClick(bookmark: PdfBookmark, event: MouseEvent): boolean {
    event.stopPropagation();
    this.openInline(bookmark);
    return false;
  }

  private selectBookmark(
    bookmark: PdfBookmark,
    index: number,
    event: Pick<MouseEvent, 'ctrlKey' | 'metaKey' | 'shiftKey'>,
  ): void {
    if (event.ctrlKey || event.metaKey) {
      const next = new Set(this.selectedIds);
      if (next.has(bookmark.id)) next.delete(bookmark.id);
      else next.add(bookmark.id);
      this.selectedIds = next;
      this._lastSelectedIdx = index;
    } else if (event.shiftKey && this._lastSelectedIdx >= 0) {
      const visible = this.visibleBookmarks;
      const start = Math.min(this._lastSelectedIdx, index);
      const end = Math.max(this._lastSelectedIdx, index);
      const next = new Set(this.selectedIds);
      for (let i = start; i <= end; i++) {
        const candidate = visible[i];
        if (candidate) next.add(candidate.id);
      }
      this.selectedIds = next;
    } else {
      this.selectedIds = new Set([bookmark.id]);
      this._lastSelectedIdx = index;
    }
  }

  public openInlineFromRow(bookmark: PdfBookmark, event: Event): void {
    event.stopPropagation();
    this.openInline(bookmark);
  }

  public openExternalFromRow(bookmark: PdfBookmark, event: Event): void {
    event.stopPropagation();
    this.openExternal(bookmark);
  }

  public openEditFromRow(bookmark: PdfBookmark, event: Event): void {
    event.stopPropagation();
    this.openEdit(bookmark);
  }

  public stopRowInteraction(event: Event): void {
    event.stopPropagation();
  }

  public openInline(bookmark: PdfBookmark): void {
    this.inlineBookmark = bookmark;
  }

  public closeInline(): void {
    this.inlineBookmark = null;
  }

  public openExternal(bookmark: PdfBookmark): void {
    window.open(`${this.pdfUrl(bookmark)}#page=${bookmark.pageNumber}`, '_blank', 'noopener,noreferrer');
  }

  public openEdit(bookmark: PdfBookmark): void {
    this.inlineBookmark = null;
    this.editingBookmark = bookmark;
    this.editTitle = bookmark.title;
    this.editDescription = bookmark.description;
    this.editTags = bookmark.tags.map(tag => ({ ...tag }));
    this.editError = '';
  }

  public cancelEdit(): void {
    if (this.saving) return;
    this.resetEditState();
  }

  public async saveEdit(): Promise<void> {
    const bookmark = this.editingBookmark;
    if (!bookmark || this.saving) return;
    const title = this.editTitle.trim();
    if (!title) {
      this.editError = this.i18n.tr('bookmarks.titleRequired');
      return;
    }

    this.saving = true;
    this.editError = '';
    try {
      const updated = await updatePdfBookmark(
        bookmark.documentId,
        bookmark.documentFileId,
        bookmark.id,
        {
          title,
          description: this.editDescription.trim(),
          tagNames: this.editTags.map(tag => tag.name.trim()).filter(Boolean),
        },
      );
      this.bookmarks = this.bookmarks.map(candidate =>
        candidate.id === updated.id ? updated : candidate);
      this.resetEditState();
    } catch (caughtError) {
      this.editError = caughtError instanceof Error
        ? caughtError.message
        : this.i18n.tr('bookmarks.saveFailed');
    } finally {
      this.saving = false;
    }
  }

  public async deleteSelected(): Promise<void> {
    const selected = this.selectedBookmarks;
    if (selected.length === 0 || this.saving) return;

    const message = selected.length === 1
      ? this.i18n.tr('bookmarks.deleteConfirm', { title: selected[0].title })
      : this.i18n.tr('bookmarks.deleteConfirmMany', { count: selected.length });
    const confirmed = await ConfirmDialog.confirm(
      message,
      this.i18n.tr('bookmarks.deleteSelectedTitle'),
    );
    if (!confirmed) return;

    this.saving = true;
    const deletedIds = new Set<string>();
    try {
      for (const bookmark of selected) {
        await deletePdfBookmark(bookmark.documentId, bookmark.documentFileId, bookmark.id);
        deletedIds.add(bookmark.id);
        this.removeBookmarkFromState(bookmark.id);
      }
      this.closeDeletedOverlays(deletedIds);
      this.clearSelection();
    } catch (caughtError) {
      this.selectedIds = new Set([...this.selectedIds].filter(id => !deletedIds.has(id)));
      this.closeDeletedOverlays(deletedIds);
      await ConfirmDialog.alert(
        caughtError instanceof Error ? caughtError.message : this.i18n.tr('bookmarks.deleteFailed'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.saving = false;
    }
  }

  private removeBookmarkFromState(bookmarkId: string): void {
    this.bookmarks = this.bookmarks.filter(candidate => candidate.id !== bookmarkId);
    const next = new Set(this.selectedIds);
    next.delete(bookmarkId);
    this.selectedIds = next;
    this._lastSelectedIdx = -1;
  }

  private closeDeletedOverlays(deletedIds: Set<string>): void {
    if (this.inlineBookmark && deletedIds.has(this.inlineBookmark.id)) this.closeInline();
    if (this.editingBookmark && deletedIds.has(this.editingBookmark.id)) this.resetEditState();
  }

  private compareBookmarks(left: PdfBookmark, right: PdfBookmark): number {
    const direction = this.sortDescending ? -1 : 1;
    let result = 0;

    switch (this.sortBy) {
      case 'page':
        result = left.pageNumber - right.pageNumber;
        break;
      case 'createdAt':
      case 'lastUpdatedAt':
        result = Date.parse(left[this.sortBy]) - Date.parse(right[this.sortBy]);
        break;
      case 'document':
        result = left.documentTitle.localeCompare(right.documentTitle, this.i18n.getLocale(), {
          sensitivity: 'base',
          numeric: true,
        });
        break;
      case 'file':
        result = left.fileName.localeCompare(right.fileName, this.i18n.getLocale(), {
          sensitivity: 'base',
          numeric: true,
        });
        break;
      case 'selectedText':
      case 'description':
      case 'tags':
      case 'title':
      default:
        result = this.sortTextValue(left, this.sortBy)
          .localeCompare(this.sortTextValue(right, this.sortBy), this.i18n.getLocale(), {
            sensitivity: 'base',
            numeric: true,
          });
        break;
    }

    return result === 0
      ? left.id.localeCompare(right.id)
      : result * direction;
  }

  private sortTextValue(bookmark: PdfBookmark, column: string): string {
    switch (column) {
      case 'selectedText': return bookmark.selectedText;
      case 'description': return bookmark.description;
      case 'tags': return bookmark.tags.map(tag => tag.name).join(', ');
      case 'document': return bookmark.documentTitle;
      case 'file': return bookmark.fileName;
      default: return bookmark.title;
    }
  }

  private saveUiState(): void {
    localStorage.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(this.getUiState()));
    this.schedulePreferenceSync();
  }

  private getUiState(): BookmarkUiState {
    return {
      showFilterBar: this.showFilterBar,
      filterText: this.filterText,
      filterTags: this.filterTags,
      viewMode: this.viewMode,
      sortBy: this.sortBy,
      sortDescending: this.sortDescending,
    };
  }

  private loadCachedPreferences(): { hasColumns: boolean; hasUiState: boolean } {
    let hasColumns = false;
    let hasUiState = false;
    const savedColumns = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (savedColumns) {
      hasColumns = true;
      this.applyColumnsSetting(savedColumns);
    }
    const savedUiState = localStorage.getItem(UI_STATE_STORAGE_KEY);
    if (savedUiState) {
      hasUiState = true;
      this.applyUiStateSetting(savedUiState);
    }
    return { hasColumns, hasUiState };
  }

  private applyColumnsSetting(value: string): void {
    try {
      const parsed = JSON.parse(value) as Record<string, boolean>;
      this.visibleColumns = { ...DEFAULT_COLUMNS, ...parsed };
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(this.visibleColumns));
    } catch {
      // Keep defaults when a preference is malformed.
    }
  }

  private applyUiStateSetting(value: string): void {
    try {
      const ui = JSON.parse(value) as BookmarkUiState;
      if (typeof ui.showFilterBar === 'boolean') this.showFilterBar = ui.showFilterBar;
      if (typeof ui.filterText === 'string') this.filterText = ui.filterText;
      if (Array.isArray(ui.filterTags)) this.filterTags = ui.filterTags;
      if (ui.viewMode === 'list' || ui.viewMode === 'tiles') this.viewMode = ui.viewMode;
      if (typeof ui.sortBy === 'string' &&
          BOOKMARK_COLUMN_DEFS.some(column => column.key === ui.sortBy)) {
        this.sortBy = ui.sortBy;
      }
      if (typeof ui.sortDescending === 'boolean') this.sortDescending = ui.sortDescending;
      localStorage.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(this.getUiState()));
    } catch {
      // Keep defaults when a preference is malformed.
    }
  }

  private schedulePreferenceSync(): void {
    if (this._preferenceSyncTimer !== null) clearTimeout(this._preferenceSyncTimer);
    this._preferenceSyncTimer = setTimeout(() => {
      this._preferenceSyncTimer = null;
      void this.syncPreferences();
    }, 500);
  }

  private async syncPreferences(): Promise<void> {
    await Promise.all([
      upsertUserSetting(COLUMNS_SETTING_KEY, JSON.stringify(this.visibleColumns)),
      upsertUserSetting(UI_STATE_SETTING_KEY, JSON.stringify(this.getUiState())),
    ].map(operation => operation.catch(() => {
      // Local storage keeps preferences usable when the server is unavailable.
    })));
  }

  private resetEditState(): void {
    this.editingBookmark = null;
    this.editTags = [];
    this.editTitle = '';
    this.editDescription = '';
    this.editError = '';
  }
}
